import { useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Phone,
  Mail,
  Search,
  Menu,
  X,
} from "lucide-react";
import logo from "../assets/logo.webp";
import { Link, useNavigate } from "react-router-dom";

function LocationNavbar() {
  const [showSearch, setShowSearch] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const navigate = useNavigate();

  const container = "max-w-[1380px] mx-auto px-4 sm:px-6";

  // Routes for Resources
  const resourceLinks = [
    { label: "Blog", to: "/blog" },
    { label: "FAQs", to: "/faqs" },
    { label: "Reviews", to: "/reviews" },
    { label: "Case Studies", to: "/case-studies" },
  ];

  // Routes for team members
  const teamLinks = [
    { label: "Aman", to: "/our-team/aman" },
    { label: "Sunaina", to: "/our-team/sunaina" },
    { label: "Shikha", to: "/our-team/shikha" },
    { label: "Gurkiran", to: "/our-team/gurkiran" },
    { label: "Samridhi", to: "/our-team/samridhi" },
    { label: "Aarti", to: "/our-team/aarti" },
  ];

  const searchRoutes = {
    home: "/",
    "everything we sell": "/everything-we-sell",

    // ---- LIFE / LIVING BENEFITS ----
    "life insurance": "/life-insurance",
    life: "/life-insurance",

    "term life": "/term-life-insurance",
    "term life insurance": "/term-life-insurance",

    "permanent life": "/permanent-life-insurance",
    "permanent life insurance": "/permanent-life-insurance",

    "whole life": "/whole-life-insurance",
    "whole life insurance": "/whole-life-insurance",

    "universal life": "/universal-life-insurance",
    "universal life insurance": "/universal-life-insurance",

    "participating permanent life": "/participating-permanent-life-insurance",
    "participating permanent life insurance":
      "/participating-permanent-life-insurance",

    "mortgage insurance": "/mortgage-insurance",

    "no medical": "/no-medical-insurance",
    "no medical insurance": "/no-medical-insurance",

    "children insurance": "/children-insurance",
    "child insurance": "/children-insurance",

    "health insurance": "/health-insurance",

    "critical illness": "/critical-illness-insurance",
    "critical illness insurance": "/critical-illness-insurance",

    "disability insurance": "/disability-insurance",
    disability: "/disability-insurance",

    // ---- TRAVEL ----
    "visitor insurance": "/visitor-to-canada-insurance",
    "visitors insurance": "/visitor-to-canada-insurance",
    "visitor to canada": "/visitor-to-canada-insurance",
    "visitor to canada insurance": "/visitor-to-canada-insurance",

    "super visa": "/super-visa-insurance",
    "super visa insurance": "/super-visa-insurance",

    "newcomers insurance": "/newcomers-insurance",
    "newcomer insurance": "/newcomers-insurance",

    "international student": "/international-student-insurance",
    "international student insurance": "/international-student-insurance",

    "travel insurance": "/travel-insurance",

    // ---- INVESTMENTS ----
    "first home savings account": "/first-home-savings-account",
    fhsa: "/first-home-savings-account",

    "registered education savings plan": "/registered-education-savings-plan",
    resp: "/registered-education-savings-plan",

    "tax free savings account": "/tax-free-savings-account",
    tfsa: "/tax-free-savings-account",

    "registered retirement savings plan": "/registered-retirement-savings-plan",
    "registered retirement savings plans":
      "/registered-retirement-savings-plan",
    rrsp: "/registered-retirement-savings-plan",

    "registered disability savings plan": "/registered-disability-savings-plan",
    rdsp: "/registered-disability-savings-plan",

    // ---- RESOURCES ----
    blog: "/blog",
    faqs: "/faqs",
    faq: "/faqs",
    reviews: "/reviews",
    "case studies": "/case-studies",

    // ---- CONTACT / TEAM ----
    contact: "/contact-us",
    "contact us": "/contact-us",
    "our team": "/our-team",

    aman: "/our-team/aman",
    sunaina: "/our-team/sunaina",
    shikha: "/our-team/shikha",
    gurkiran: "/our-team/gurkiran",
    samridhi: "/our-team/samridhi",
    aarti: "/our-team/aarti",

    // ---- BLOG DETAILS (dynamic) ----
    "blog post": "/blog",
    "blog details": "/blog",

    // ---- GET QUOTE PAGES ----
    "life quote": "https://compulife.org/website/655/quoter.html",
    "life insurance quote": "https://compulife.org/website/655/quoter.html",
    "get quote life": "https://compulife.org/website/655/quoter.html",

    "visitors quote": "/quote/vtc",
    "visitor insurance quote": "/quote/vtc",
    "get quote visitors": "/quote/vtc",
  };

  const handleSearchKeyDown = (e) => {
    if (e.key !== "Enter") return;

    const term = searchTerm.trim().toLowerCase();
    if (!term) return;

    const matchKey = Object.keys(searchRoutes).find((key) => {
      const k = key.toLowerCase();
      return (
        k === term ||
        k.startsWith(term) ||
        k.includes(term) ||
        term.startsWith(k) ||
        term.includes(k)
      );
    });

    if (matchKey) {
      const target = searchRoutes[matchKey];

      if (target.startsWith("http")) {
        window.location.href = target;
      } else {
        navigate(target);
      }

      setSearchTerm("");
      setShowSearch(false);
      setMobileOpen(false);
    } else {
      navigate(`/blog/${encodeURIComponent(term)}`);

      setSearchTerm("");
      setShowSearch(false);
      setMobileOpen(false);
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 w-full z-[999] text-white bg-[#49628B] shadow-[0_10px_30px_rgba(0,0,0,0.18)]">
      {/* Top bar */}
      <div className={container}>
        <div className="flex flex-wrap justify-center md:justify-end items-center gap-x-4 gap-y-1 text-sm py-2 tracking-wide">
          <a
            href="tel:+18444400128"
            className="flex items-center gap-1 hover:text-sky-200 text-white/90"
          >
            <Phone size={15} className="text-sky-200" />
            <span className="text-[15px]">(844) 440-0128</span>
          </a>

          <span className="hidden md:inline mx-4 text-white/25">|</span>

          <a
            href="mailto:info@einsured.ca"
            className="flex items-center gap-1 hover:text-sky-200 text-white/90"
          >
            <Mail size={15} className="text-sky-200" />
            <span className="text-[15px]">info@einsured.ca</span>
          </a>

          <span className="hidden md:inline mx-4 text-white/25">|</span>
        </div>
      </div>

      <div className={container}>
        <div className="flex items-center justify-between py-3 md:py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img
              src={logo}
              alt="einsured.ca logo"
              width={160}
              height={118}
              className="h-12 md:h-14 w-auto"
            />
          </Link>

          {/* Desktop menu */}
          <ul className="hidden md:flex items-center space-x-8 lg:space-x-10 font-medium text-[16px]">
            {/* Home */}
            <li className="relative group cursor-pointer hover:text-white">
              <div className="flex items-center gap-1">
                <Link
                  to="/"
                  className="font-semibold text-white hover:text-sky-200 transition"
                >
                  Home
                </Link>
              </div>
              <div className="absolute left-0 top-full bg-[#3f567b] rounded shadow-lg w-56 py-2 hidden group-hover:block z-50 text-[15px]">
                <Link
                  to="/everything-we-sell"
                  className="block px-4 py-2 hover:bg-[#2f4668]"
                >
                  Everything We Sell
                </Link>
              </div>
            </li>

            {/* Insurance */}
            <li className="relative group cursor-pointer hover:text-white">
              <div className="flex items-center gap-1">
                <span className="font-semibold text-white">Insurance</span>
                <ChevronDown size={16} />
              </div>

              <div className="absolute left-0 top-full bg-[#3f567b] rounded shadow-lg w-64 py-2 hidden group-hover:block z-50 text-[15px]">
                {/* Life */}
                <div className="relative group/life flex justify-between px-4 py-2 hover:bg-[#2f4668] cursor-pointer">
                  <span>Life</span>
                  <ChevronRight size={14} />
                  <div className="absolute left-full top-0 bg-[#3f567b] rounded shadow-lg w-72 py-2 hidden group-hover/life:block z-50">
                    <Link
                      to="/life-insurance"
                      className="block px-4 py-2 hover:bg-[#2f4668]"
                    >
                      Life Insurance
                    </Link>
                    <Link
                      to="/term-life-insurance"
                      className="block px-4 py-2 hover:bg-[#2f4668]"
                    >
                      Term Life Insurance
                    </Link>

                    {/* Permanent Life submenu */}
                    <div className="relative group/permanent flex justify-between px-4 py-2 hover:bg-[#2f4668] cursor-pointer">
                      <Link
                        to="/permanent-life-insurance"
                        className="flex justify-between items-center w-full"
                      >
                        <span>Permanent Life Insurance</span>
                        <ChevronRight size={14} />
                      </Link>
                      <div className="absolute left-full top-0 bg-[#3f567b] rounded shadow-lg w-72 py-2 hidden group-hover/permanent:block z-50">
                        <Link
                          to="/whole-life-insurance"
                          className="block px-4 py-2 hover:bg-[#2f4668]"
                        >
                          Whole Life Insurance
                        </Link>
                        <Link
                          to="/universal-life-insurance"
                          className="block px-4 py-2 hover:bg-[#2f4668]"
                        >
                          Universal Life Insurance
                        </Link>
                        <Link
                          to="/participating-permanent-life-insurance"
                          className="block px-4 py-2 hover:bg-[#2f4668]"
                        >
                          Participating Permanent Life Insurance
                        </Link>
                      </div>
                    </div>

                    <Link
                      to="/mortgage-insurance"
                      className="block px-4 py-2 hover:bg-[#2f4668]"
                    >
                      Mortgage Insurance
                    </Link>
                    <Link
                      to="/no-medical-insurance"
                      className="block px-4 py-2 hover:bg-[#2f4668]"
                    >
                      No Medical Insurance
                    </Link>
                    <Link
                      to="/children-insurance"
                      className="block px-4 py-2 hover:bg-[#2f4668]"
                    >
                      Children Insurance
                    </Link>
                    <Link
                      to="/health-insurance"
                      className="block px-4 py-2 hover:bg-[#2f4668]"
                    >
                      Health Insurance
                    </Link>
                  </div>
                </div>

                {/* Travel */}
                <div className="relative group/travel flex justify-between px-4 py-2 hover:bg-[#2f4668] cursor-pointer">
                  <span>Travel</span>
                  <ChevronRight size={14} />
                  <div className="absolute left-full top-0 bg-[#3f567b] rounded shadow-lg w-64 py-2 hidden group-hover/travel:block z-50">
                    <Link
                      to="/visitor-to-canada-insurance"
                      className="block px-4 py-2 hover:bg-[#2f4668] text-white"
                    >
                      Visitor to Canada Insurance
                    </Link>
                    <Link
                      to="/super-visa-insurance"
                      className="block px-4 py-2 hover:bg-[#2f4668] text-white"
                    >
                      Super Visa Insurance
                    </Link>
                    <Link
                      to="/newcomers-insurance"
                      className="block px-4 py-2 hover:bg-[#2f4668] text-white"
                    >
                      Newcomers Insurance
                    </Link>
                    <Link
                      to="/international-student-insurance"
                      className="block px-4 py-2 hover:bg-[#2f4668] text-white"
                    >
                      International Student Insurance
                    </Link>
                    <Link
                      to="/travel-insurance"
                      className="block px-4 py-2 hover:bg-[#2f4668] text-white"
                    >
                      Travel Insurance
                    </Link>
                  </div>
                </div>

                {/* Living Benefits */}
                <div className="relative group/living flex justify-between px-4 py-2 hover:bg-[#2f4668] cursor-pointer">
                  <span>Living Benefits</span>
                  <ChevronRight size={14} />
                  <div className="absolute left-full top-0 bg-[#3f567b] rounded shadow-lg w-60 py-2 hidden group-hover/living:block z-50">
                    <Link
                      to="/critical-illness-insurance"
                      className="block px-4 py-2 hover:bg-[#2f4668] text-white"
                    >
                      Critical Illness Insurance
                    </Link>
                    <Link
                      to="/disability-insurance"
                      className="block px-4 py-2 hover:bg-[#2f4668] text-white"
                    >
                      Disability Insurance
                    </Link>
                  </div>
                </div>
              </div>
            </li>

            {/* Investments (DESKTOP) */}
            <li className="relative group cursor-pointer hover:text-white">
              <div className="flex items-center gap-1">
                <span className="font-semibold text-white">Investments</span>
                <ChevronDown size={16} />
              </div>
              <div className="absolute left-0 top-full bg-[#3f567b] rounded shadow-lg w-[390px] py-2 hidden group-hover:block z-50 text-[15px]">
                <Link
                  to="/first-home-savings-account"
                  className="block px-4 py-2 hover:bg-[#2f4668]"
                >
                  First Home Savings Account (FHSA)
                </Link>
                <Link
                  to="/registered-education-savings-plan"
                  className="block px-4 py-2 hover:bg-[#2f4668]"
                >
                  Registered Education Savings Plan (RESP)
                </Link>
                <Link
                  to="/tax-free-savings-account"
                  className="block px-4 py-2 hover:bg-[#2f4668]"
                >
                  Tax-Free Savings Account (TFSA)
                </Link>
                <Link
                  to="/registered-retirement-savings-plan"
                  className="block px-4 py-2 hover:bg-[#2f4668]"
                >
                  Registered Retirement Savings Plan (RRSP)
                </Link>
                <Link
                  to="/registered-disability-savings-plan"
                  className="block px-4 py-2 hover:bg-[#2f4668]"
                >
                  Registered Disability Savings Plan (RDSP)
                </Link>
              </div>
            </li>

            {/* Resources (DESKTOP) */}
            <li className="relative group cursor-pointer hover:text-white">
              <div className="flex items-center gap-1">
                <span className="font-semibold text-white">Resources</span>
                <ChevronDown size={16} />
              </div>
              <div className="absolute left-0 top-full bg-[#3f567b] rounded shadow-lg w-56 py-2 hidden group-hover:block z-50 text-[15px]">
                {resourceLinks.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="block px-4 py-2 hover:bg-[#2f4668]"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </li>

            {/* Contact Us (DESKTOP) */}
            <li className="relative group cursor-pointer hover:text-white">
              <div className="flex items-center gap-1">
                <span className="font-semibold text-white">Contact Us</span>
                <ChevronDown size={16} />
              </div>
              <div className="absolute left-0 top-full bg-[#3f567b] rounded shadow-lg w-56 py-2 hidden group-hover:block z-50 text-[15px]">
                <div className="relative group/team px-4 py-2 hover:bg-[#2f4668] cursor-pointer">
                  <Link
                    to="/our-team"
                    className="flex justify-between items-center w-full"
                  >
                    <span>Our Team</span>
                    <ChevronRight size={14} />
                  </Link>
                  <div className="absolute left-full top-0 bg-[#3f567b] rounded shadow-lg w-48 py-2 hidden group-hover/team:block z-50">
                    {teamLinks.map((member) => (
                      <Link
                        key={member.to}
                        to={member.to}
                        className="block px-4 py-2 hover:bg-[#2f4668]"
                      >
                        {member.label}
                      </Link>
                    ))}
                  </div>
                </div>

                <Link
                  to="/contact-us"
                  className="block px-4 py-2 hover:bg-[#2f4668]"
                >
                  Contact Us
                </Link>
              </div>
            </li>
          </ul>

          {/* Right side (desktop) */}
          <div className="hidden md:flex items-center space-x-4 relative">
            <div className="relative">
              <button
                onClick={() => setShowSearch((prev) => !prev)}
                className="cursor-pointer text-white/90 hover:text-white transition"
                aria-expanded={showSearch}
                aria-label="Search"
              >
                <Search size={20} />
              </button>
              {showSearch && (
                <div className="absolute right-0 top-full mt-1 flex items-center bg-[#3f567b] rounded shadow-lg px-2 py-1 z-50">
                  <input
                    type="text"
                    placeholder="Search..."
                    autoFocus
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    onBlur={() => setTimeout(() => setShowSearch(false), 200)}
                    className="bg-transparent border border-white/40 text-white text-sm px-2 py-1 rounded focus:outline-none w-44"
                  />
                </div>
              )}
            </div>

            <div className="relative group">
              <button className="bg-sky-400 hover:bg-sky-500 text-white font-semibold px-6 py-2 rounded-full inline-flex items-center gap-1 text-[15px] transition">
                Get Quote
                <ChevronDown size={16} />
              </button>
              <div className="absolute right-0 top-full bg-[#3f567b] rounded shadow-lg w-56 py-2 hidden group-hover:block z-50 text-[15px]">
                <a
                  href="https://compulife.org/website/655/quoter.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-4 py-2 hover:bg-[#2f4668]"
                >
                  Life Insurance
                </a>
                <Link
                  to="/quote/vtc/"
                  className="block px-4 py-2 hover:bg-[#2f4668]"
                >
                  Visitors Insurance
                </Link>
              </div>
            </div>
          </div>

          {/* Mobile */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              className="p-2"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setMobileOpen(false)}
          />
          <div className="fixed top-0 right-0 h-full w-[88%] sm:w-[420px] bg-[#49628B] text-white shadow-xl z-[60] overflow-y-auto">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <img src={logo} alt="einsured.ca logo" width={160} height={118} className="h-10 w-auto" />
              <div className="flex items-center gap-2">
                <a
                  href="tel:+18444400128"
                  className="px-3 py-1.5 rounded bg-white/10 hover:bg-white/15 text-sm"
                >
                  Call
                </a>
                <a
                  href="mailto:info@einsured.ca"
                  className="px-3 py-1.5 rounded bg-white/10 hover:bg-white/15 text-sm"
                >
                  Email
                </a>
                <button
                  onClick={() => setMobileOpen(false)}
                  aria-label="Close menu"
                  className="p-2 rounded hover:bg-white/10"
                >
                  <X size={22} />
                </button>
              </div>
            </div>

            <div className="p-4">
              <div className="flex items-center bg-white/10 rounded px-2 py-2">
                <Search size={18} className="mr-2" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  className="bg-transparent text-white text-sm focus:outline-none w-full"
                />
              </div>
            </div>

            <nav className="px-2 pb-6 text-[15px] space-y-2">
              <details>
                <summary className="list-none px-3 py-3 rounded hover:bg-white/10 flex justify-between items-center">
                  <span className="font-semibold">Home</span>
                  <ChevronDown size={18} />
                </summary>
                <div className="pl-4 pb-2">
                  <Link
                    to="/"
                    onClick={() => setMobileOpen(false)}
                    className="block px-2 py-2 rounded hover:bg-white/10"
                  >
                    Home
                  </Link>
                  <Link
                    to="/everything-we-sell"
                    onClick={() => setMobileOpen(false)}
                    className="block px-2 py-2 rounded hover:bg-white/10"
                  >
                    Everything We Sell
                  </Link>
                </div>
              </details>

              <details>
                <summary className="list-none px-3 py-3 rounded hover:bg-white/10 flex justify-between items-center">
                  <span className="font-semibold">Insurance</span>
                  <ChevronDown size={18} />
                </summary>

                <div className="pl-4">
                  <details>
                    <summary className="list-none px-2 py-2 rounded hover:bg-white/10 flex justify-between items-center">
                      <span>Life</span>
                      <ChevronRight size={18} />
                    </summary>
                    <div className="pl-3 pb-2">
                      <Link
                        to="/life-insurance"
                        onClick={() => setMobileOpen(false)}
                        className="block px-2 py-2 rounded hover:bg-white/10"
                      >
                        Life Insurance
                      </Link>
                      <Link
                        to="/term-life-insurance"
                        onClick={() => setMobileOpen(false)}
                        className="block px-2 py-2 rounded hover:bg-white/10"
                      >
                        Term Life Insurance
                      </Link>

                      <details>
                        <summary className="list-none px-2 py-2 rounded hover:bg-white/10 flex justify-between items-center">
                          <span>Permanent Life Insurance</span>
                          <ChevronRight size={18} />
                        </summary>
                        <div className="pl-3 pb-2">
                          <Link
                            to="/whole-life-insurance"
                            onClick={() => setMobileOpen(false)}
                            className="block px-2 py-2 rounded hover:bg-white/10"
                          >
                            Whole Life Insurance
                          </Link>
                          <Link
                            to="/universal-life-insurance"
                            onClick={() => setMobileOpen(false)}
                            className="block px-2 py-2 rounded hover:bg-white/10"
                          >
                            Universal Life Insurance
                          </Link>
                          <Link
                            to="/participating-permanent-life-insurance"
                            onClick={() => setMobileOpen(false)}
                            className="block px-2 py-2 rounded hover:bg-white/10"
                          >
                            Participating Permanent Life Insurance
                          </Link>
                        </div>
                      </details>

                      <Link
                        to="/mortgage-insurance"
                        onClick={() => setMobileOpen(false)}
                        className="block px-2 py-2 rounded hover:bg-white/10"
                      >
                        Mortgage Insurance
                      </Link>
                      <Link
                        to="/no-medical-insurance"
                        onClick={() => setMobileOpen(false)}
                        className="block px-2 py-2 rounded hover:bg-white/10"
                      >
                        No Medical Insurance
                      </Link>
                      <Link
                        to="/children-insurance"
                        onClick={() => setMobileOpen(false)}
                        className="block px-2 py-2 rounded hover:bg-white/10"
                      >
                        Children Insurance
                      </Link>
                      <Link
                        to="/health-insurance"
                        onClick={() => setMobileOpen(false)}
                        className="block px-2 py-2 rounded hover:bg-white/10"
                      >
                        Health Insurance
                      </Link>
                    </div>
                  </details>

                  <details>
                    <summary className="list-none px-2 py-2 rounded hover:bg-white/10 flex justify-between items-center">
                      <span>Travel</span>
                      <ChevronRight size={18} />
                    </summary>
                    <div className="pl-3 pb-2">
                      <Link
                        to="/visitor-to-canada-insurance"
                        onClick={() => setMobileOpen(false)}
                        className="block px-2 py-2 rounded hover:bg-white/10"
                      >
                        Visitor to Canada Insurance
                      </Link>
                      <Link
                        to="/super-visa-insurance"
                        onClick={() => setMobileOpen(false)}
                        className="block px-2 py-2 rounded hover:bg-white/10"
                      >
                        Super Visa Insurance
                      </Link>
                      <Link
                        to="/newcomers-insurance"
                        onClick={() => setMobileOpen(false)}
                        className="block px-2 py-2 rounded hover:bg-white/10"
                      >
                        Newcomers Insurance
                      </Link>
                      <Link
                        to="/international-student-insurance"
                        onClick={() => setMobileOpen(false)}
                        className="block px-2 py-2 rounded hover:bg-white/10"
                      >
                        International Student Insurance
                      </Link>
                      <Link
                        to="/travel-insurance"
                        onClick={() => setMobileOpen(false)}
                        className="block px-2 py-2 rounded hover:bg-white/10"
                      >
                        Travel Insurance
                      </Link>
                    </div>
                  </details>

                  <details>
                    <summary className="list-none px-2 py-2 rounded hover:bg-white/10 flex justify-between items-center">
                      <span>Living Benefits</span>
                      <ChevronRight size={18} />
                    </summary>
                    <div className="pl-3 pb-2">
                      <Link
                        to="/critical-illness-insurance"
                        onClick={() => setMobileOpen(false)}
                        className="block px-2 py-2 rounded hover:bg-white/10"
                      >
                        Critical Illness Insurance
                      </Link>
                      <Link
                        to="/disability-insurance"
                        onClick={() => setMobileOpen(false)}
                        className="block px-2 py-2 rounded hover:bg-white/10"
                      >
                        Disability Insurance
                      </Link>
                    </div>
                  </details>
                </div>
              </details>

              <details>
                <summary className="list-none px-3 py-3 rounded hover:bg-white/10 flex justify-between items-center">
                  <span className="font-semibold">Investments</span>
                  <ChevronDown size={18} />
                </summary>
                <div className="pl-4 pb-2">
                  <Link
                    to="/first-home-savings-account"
                    onClick={() => setMobileOpen(false)}
                    className="block px-2 py-2 rounded hover:bg-white/10"
                  >
                    First Home Savings Account (FHSA)
                  </Link>
                  <Link
                    to="/registered-education-savings-plan"
                    onClick={() => setMobileOpen(false)}
                    className="block px-2 py-2 rounded hover:bg-white/10"
                  >
                    Registered Education Savings Plan (RESP)
                  </Link>
                  <Link
                    to="/tax-free-savings-account"
                    onClick={() => setMobileOpen(false)}
                    className="block px-2 py-2 rounded hover:bg-white/10"
                  >
                    Tax-Free Savings Account (TFSA)
                  </Link>
                  <Link
                    to="/registered-retirement-savings-plan"
                    onClick={() => setMobileOpen(false)}
                    className="block px-2 py-2 rounded hover:bg-white/10"
                  >
                    Registered Retirement Savings Plan (RRSP)
                  </Link>
                  <Link
                    to="/registered-disability-savings-plan"
                    onClick={() => setMobileOpen(false)}
                    className="block px-2 py-2 rounded hover:bg-white/10"
                  >
                    Registered Disability Savings Plan (RDSP)
                  </Link>
                </div>
              </details>

              <details>
                <summary className="list-none px-3 py-3 rounded hover:bg-white/10 flex justify-between items-center">
                  <span className="font-semibold">Resources</span>
                  <ChevronDown size={18} />
                </summary>
                <div className="pl-4 pb-2">
                  {resourceLinks.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setMobileOpen(false)}
                      className="block px-2 py-2 rounded hover:bg-white/10"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </details>

              <details>
                <summary className="list-none px-3 py-3 rounded hover:bg-white/10 flex justify-between items-center">
                  <span className="font-semibold">Contact Us</span>
                  <ChevronDown size={18} />
                </summary>
                <div className="pl-4 pb-2">
                  <details>
                    <summary className="list-none px-2 py-2 rounded hover:bg-white/10 flex justify-between items-center">
                      <span>Our Team</span>
                      <ChevronRight size={18} />
                    </summary>
                    <div className="pl-3 pb-2">
                      {teamLinks.map((member) => (
                        <Link
                          key={member.to}
                          to={member.to}
                          onClick={() => setMobileOpen(false)}
                          className="block px-2 py-2 rounded hover:bg-white/10"
                        >
                          {member.label}
                        </Link>
                      ))}
                    </div>
                  </details>

                  <Link
                    to="/contact-us"
                    onClick={() => setMobileOpen(false)}
                    className="block px-2 py-2 rounded hover:bg-white/10"
                  >
                    Contact Us
                  </Link>
                </div>
              </details>

              <details>
                <summary className="list-none mx-2 mt-1 bg-sky-400 hover:bg-sky-500 text-sky-900 font-semibold px-4 py-3 rounded-full flex justify-between items-center">
                  <span>Get Quote</span>
                  <ChevronDown size={18} />
                </summary>
                <div className="pl-4 pb-2 mt-2">
                  <a
                    href="https://compulife.org/website/655/quoter.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMobileOpen(false)}
                    className="block px-2 py-2 rounded hover:bg-white/10"
                  >
                    Life Insurance
                  </a>
                  <Link
                    to="/quote/vtc/"
                    onClick={() => setMobileOpen(false)}
                    className="block px-2 py-2 rounded hover:bg-white/10"
                  >
                    Visitors Insurance
                  </Link>
                </div>
              </details>
            </nav>
          </div>
        </>
      )}
    </nav>
  );
}

export default LocationNavbar;